import * as React from 'react'
import { UserDto } from '../../dtos/user.dto'
import { validateSession } from '../../_utils/session-helpers'
import { useService } from '@pushly/aqe/lib/hooks'
import { SessionState } from '@pushly/aqe'
import { AppState } from '../../stores/app.state'
import { AppService } from '../../services/index'
import { Dispatch, useCallback, useReducer } from 'react'
import {
    AUTH0_TENANT_ID,
    AUTH0_CLIENT_ID,
    AUTH0_PLATFORM_REDIRECT_PATHNAME,
    AUTH0_STATE_SESSION_KEY,
} from '../../constants'
import axios from 'axios'
import aqe from '@pushly/aqe'
import { useHistory, useLocation } from 'react-router-dom'
import { parseQueryStringParams } from '../../_utils/query-string'
import { ORGANIZATIONS_ROUTE } from '@halo/portals/post-auth/accounts'
import { LoadingScreen } from '@halo/components/loading-screen/loading-screen'
import { SessionValidationFailedException } from '@halo/exceptions/session-validation.exception'
import { broadcastSessionRestored } from '@pushly/aqe/lib/utils/session/session-state-sync'
import { Auth0InvalidAuthorizationParamsException } from '@halo/exceptions/auth0-invalid-authorization-params.exception'
import { ErrorPage } from '@halo/components/error-page/error-page'
import {
    PlatformUserContext,
    PlatformUserDispatchContext,
    platformUserReducer,
    UserContextAction,
    UserPreferencesContext,
} from '@pushly/aqe/lib/providers/user-preferences-provider/internal'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { Container } from 'typescript-ioc'
import { PreferenceService } from '@pushly/aqe/lib/services/preference.service'
import { PlatformUserDomainRecord } from '@halo/models/domains/platform-user-domain-record'

export type PlatformUser =
    | {
          authenticated: false
          revalidate?: boolean
      }
    | {
          authenticated: true
          user: UserDto
          revalidate?: boolean
      }

type UserContext = PlatformUser & { assignUser?: Dispatch<PlatformUser> }

const initialPlatformUser: PlatformUser = { authenticated: false }
// export const PlatformUserContext = React.createContext<UserContext>(initialPlatformUser)

export const UserProvider: React.FC<any> = ({ children, ...props }) => {
    const loc = useLocation()

    const appState = useService(AppState)
    const appService = useService(AppService)
    const sessionState = useService(SessionState)
    const preferenceService = Container.get(PreferenceService)

    const history = useHistory()
    appService.attachHistoryContainer(history)

    const [userContext, dispatch] = useReducer(platformUserReducer, {
        authenticated: false,
        preferences: {
            state: {
                bundle: null,
                loading: true,
            },
            refresh: async () => {},
        },
        loading: false,
    })
    const [initialUserContextLoaded, setInitialUserContextLoaded] = React.useState<boolean>(false)
    const [loadError, setLoadError] = React.useState<Error | null>(null)
    const inAuth0Flow = React.useRef(false)

    const handleLoadError = React.useCallback((error: Error) => {
        setLoadError(error)

        sessionState.isAuthenticated = false
        appService.unsetAppLoading()

        setInitialUserContextLoaded(true)
    }, [])

    const authorizeAndLoadUserViaAuth0Code = React.useCallback(async (code: string, state: string) => {
        window.sessionStorage.removeItem(AUTH0_STATE_SESSION_KEY)

        const query = [
            `code=${code}`,
            `redirect_uri=${aqe.defaults.internalPlatformDomain}`,
            `tenant_id=${AUTH0_TENANT_ID}`,
            `client_id=${AUTH0_CLIENT_ID}`,
        ].join('&')

        try {
            const res = await axios.get<any>(`${aqe.defaults.publicApiDomain}/auth/token?${query}`, {
                bypassGlobalErrorHandlers: true,
            } as any)

            if (res.status === 200) {
                await setupInitialUserContext(UserDto.fromApiResponse(res.data.data))

                const redirect = localStorage.getItem('refreshRedirectURI')
                localStorage.removeItem('refreshRedirectURI')

                appService.route(redirect ?? ORGANIZATIONS_ROUTE)
            }
        } catch (error) {
            handleLoadError(error)
        } finally {
            inAuth0Flow.current = false
        }
    }, [])

    const loadUserBootstrapPreferences = useCallback(
        async (userId?: number, namespace?: string) => {
            try {
                if (!userId && !appState.currentUser?.id) {
                    return
                }

                const targetUserId = userId || appState.currentUser?.id

                dispatch({ type: UserContextAction.BOOTSTRAP_START })

                const response = await preferenceService.fetchBootstrapPreferences(targetUserId!)

                if (response.data) {
                    const bundle = response.data

                    const preferencesContext: UserPreferencesContext = {
                        state: {
                            bundle,
                            loading: false,
                        },
                        refresh: async () => {
                            return loadUserBootstrapPreferences(targetUserId, namespace)
                        },
                    }

                    dispatch({
                        type: UserContextAction.BOOTSTRAP_SUCCESS,
                        preferences: preferencesContext,
                    })
                }
            } catch (error) {
                dispatch({
                    type: UserContextAction.BOOTSTRAP_ERROR,
                    error: error.message || 'Failed to load user bootstrap preferences',
                })

                simpleNotification('error', 'Failed to load user preferences')
            }
        },
        [appState.currentUser?.id, appState.currentDomain?.id],
    )

    const loadUserViaSession = React.useCallback(async (skipValidation: boolean = false) => {
        let user: UserDto
        try {
            const validatedUser = await validateSession()
            user = UserDto.fromApiResponse(validatedUser)

            await setupInitialUserContext(user!)
        } catch (error) {
            const wrappedError = new SessionValidationFailedException(error)
            handleLoadError(wrappedError)
        }
    }, [])

    const setupInitialUserContext = React.useCallback(
        async (user: UserDto) => {
            await refreshUserContext(user)

            // Load bootstrap preferences for the user
            await loadUserBootstrapPreferences(user.id)

            if (!sessionState.isAuthenticated) {
                broadcastSessionRestored()
            }
            sessionState.isAuthenticated = true
            appService.unsetAppLoading()

            setInitialUserContextLoaded(true)
        },
        [loadUserBootstrapPreferences],
    )

    /**
     * Gets all user data and loads abilities
     * Can be called at anytime to refresh user context
     */
    const refreshUserContext = React.useCallback(async (user: UserDto) => {
        try {
            await appState.fetchUserDependencies()

            appState.currentUser = user
            await appState.abilityStore.loadAbilitiesForCurrentUser()

            const currentUserDomainIds =
                appState.currentUserDomains.map((domain: PlatformUserDomainRecord) => domain.id) ?? []

            dispatch({ type: UserContextAction.SET_AUTHENTICATED_USER, user, currentUserDomainIds })

            // TODO: migrate appState user auto refresh here
        } catch (error) {
            setLoadError(error)
        }
    }, [])

    React.useEffect(() => {
        if (!inAuth0Flow.current && loc.pathname === AUTH0_PLATFORM_REDIRECT_PATHNAME) {
            inAuth0Flow.current = true

            const params = parseQueryStringParams()
            const code = params.get('code')?.trim()
            const state = params.get('state')?.trim()
            const stateSessionValue = window.sessionStorage.getItem(AUTH0_STATE_SESSION_KEY)

            if (code && state === stateSessionValue) {
                authorizeAndLoadUserViaAuth0Code(code, state)
            } else {
                setLoadError(new Auth0InvalidAuthorizationParamsException())
            }
        }
    }, [inAuth0Flow.current, loc.pathname])

    React.useEffect(() => {
        // prevent unnecessary revalidate user, until code has been traded for auth access token - causing early
        // unset loading states / behaviors
        if (!inAuth0Flow.current) {
            loadUserViaSession()
        }
    }, [inAuth0Flow.current])

    if (loadError instanceof Auth0InvalidAuthorizationParamsException) {
        return <ErrorPage errorCode={404} showAction={true} hideLogo={false} error={loadError} />
    } else if (loadError && !(loadError instanceof SessionValidationFailedException)) {
        return <ErrorPage errorCode={403} showAction={true} hideLogo={false} error={loadError} />
    } else if (!initialUserContextLoaded) {
        return <LoadingScreen loading={true} />
    }

    return (
        <PlatformUserContext.Provider value={{ ...userContext }}>
            <PlatformUserDispatchContext.Provider value={dispatch}>{children}</PlatformUserDispatchContext.Provider>
        </PlatformUserContext.Provider>
    )
}
